import { useEffect, useState } from 'react';
import { isDesktopHost } from '../utils/host';
import { bossToggle, setBossKeysOnHost } from '../utils/desktopWindows';
import {
  BOSS_KEY_CHANGED_EVENT, createBossKeyMatcher, getBossKey, isBossKeyRecording, toVirtualKeys,
} from '../utils/bossKey';

/**
 * 老板键与宿主之间的**桥**：两件事都要做，原因是实测出来的。
 *
 * ## 1. 把组合下发给 Rust（`boss-set`）
 *
 * Rust 侧的 `WH_KEYBOARD_LL` 钩子负责"别的应用在前台"时识别（它看得到整个会话的按键，
 * 而且窗口藏起来之后页面收不到键盘，只有它能救回来）。
 *
 * ## 2. 应用**自己**在前台时，由这里识别
 *
 * ⚠️ **实测现象**：我们的窗口获得焦点时，那个系统级钩子**收不到按键**（累计按键不涨），
 * 切到别的应用则完全正常。于是"在自己应用里按组合键"这条路必须由页面补上：
 * 页面监听 keydown/keyup → 用与 Rust 同语义的匹配器判定（`createBossKeyMatcher`）→
 * 命中就调 `boss-toggle`。
 *
 * 两条路都有防抖（Rust `toggle_all` 400ms、这里 500ms），所以即使同时到达也不会
 * "收起又立刻恢复"（那种现象看起来就像"完全没动作"）。
 */
export function BossKeyBridge(): null {
  const [keys, setKeys] = useState<string[] | null>(() => getBossKey());

  // 设置页改完（同窗口事件）或另一个窗口改了（storage 事件）都要重新读
  useEffect(() => {
    const refresh = () => setKeys(getBossKey());
    window.addEventListener(BOSS_KEY_CHANGED_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(BOSS_KEY_CHANGED_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  // 1) 下发给 Rust 的全局钩子
  useEffect(() => {
    if (!isDesktopHost()) return;
    const push = () => { void setBossKeysOnHost(toVirtualKeys(getBossKey() ?? [])); };
    push();
    window.addEventListener(BOSS_KEY_CHANGED_EVENT, push);
    window.addEventListener('storage', push);
    return () => {
      window.removeEventListener(BOSS_KEY_CHANGED_EVENT, push);
      window.removeEventListener('storage', push);
    };
  }, []);

  // 2) 应用内的识别（我们自己的窗口在前台时钩子收不到按键）
  useEffect(() => {
    if (!isDesktopHost() || !keys || keys.length === 0) return;
    const matcher = createBossKeyMatcher(keys);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.repeat) return;
      // 正在录制组合：不要把用户录的键当成触发（否则录 Ctrl+Ctrl 会顺手把应用收起来）
      if (isBossKeyRecording()) return;
      if (!event.keyCode) return;
      if (!matcher.keyDown(event.keyCode)) return;
      // 命中：不让这一下继续传给编辑器，然后请求收起/恢复
      event.preventDefault();
      event.stopPropagation();
      void bossToggle();
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.keyCode) matcher.keyUp(event.keyCode);
    };
    // 捕获阶段：先于编辑器拿到按键（命中时才拦截）
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', onKeyUp, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', onKeyUp, true);
    };
  }, [keys]);

  return null;
}
